import {
  AccountBalance,
  Assignment,
  AssignmentInd,
  AttachMoney,
  BusinessCenter,
  CalendarToday,
  Description,
  LocationOn,
  Person,
} from '@mui/icons-material';
import { Box, Chip, Divider, Grid, Paper, Typography } from '@mui/material';
import { getPrincipalInfo } from 'lib/api/sgr';
import { SGR_STATES, SGR_TYPES } from 'lib/consts';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatAddress, formatDate, formatMoney } from 'lib/functions/utils';
import {
  Address,
  Business,
  BusinessUnit,
  Document,
  Warranty,
} from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import PaymentDocument from './PaymentDocument';
import RiskPremiumCalculator from './RiskPremiumCalculator';

type Principal = {
  business: Business;
  unit: BusinessUnit;
  address: Address;
};

const WarrantySummary: React.FC<{
  warranty: Warranty & { documents: Document[] };
}> = ({ warranty }) => {
  const dispatch = useDispatch();
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(false);

  if (!warranty) return null;

  const warrantyType = SGR_TYPES.find(
    (type) => type.value === warranty.type,
  )?.label;
  const warrantyStatus = SGR_STATES.find(
    (state) => state.value === warranty.status,
  );

  const showDocument =
    warranty?.status === 'APPROVED' || warranty?.status === 'ACTIVE';

  const showCalculator =
    warranty?.status === 'QUOTED' || warranty?.status === 'IN_REVIEW';

  const daysDifference = Math.ceil(
    (new Date(warranty.endDate).getTime() -
      new Date(warranty.startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );

  // Fetch mandante details using express API
  const fetchPrincipal = async () => {
    if (!warranty.id) return;
    setLoading(true);
    try {
      const principal = await getPrincipalInfo(dispatch, warranty.id);
      setPrincipal(principal);
    } catch (error) {
      console.error('Error fetching mandante details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (warranty.id) void fetchPrincipal();
  }, [warranty.id]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Box mt={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1rem',
                  lineHeight: 1.1,
                }}
              >
                Información General
              </Typography>
              <Chip
                label="Detalles"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
              <Chip
                label={warrantyStatus?.label}
                color={
                  warrantyStatus?.color as
                    | 'default'
                    | 'warning'
                    | 'info'
                    | 'success'
                    | 'error'
                }
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BusinessCenter
                    sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Tipo de Garantía
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {warrantyType}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AttachMoney
                    sx={{ fontSize: '0.8rem', color: 'success.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Monto Cotizado
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {formatMoney(warranty.quotedAmount)}{' '}
                    {warranty.quotedCurrency}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Assignment
                    sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Número de Licitación
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    #{warranty.tenderId}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(0, 150, 136, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AccountBalance
                    sx={{ fontSize: '0.8rem', color: 'info.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Fogape
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    No / -
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(121, 85, 72, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Description sx={{ fontSize: '0.8rem', color: 'brown' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Glosa
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {warranty.description ?? 'Sin descripción'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1rem',
                  lineHeight: 1.1,
                }}
              >
                Fechas Importantes
              </Typography>
              <Chip
                label="Cronología"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarToday
                    sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Fecha de Cotización
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {formatDate(new Date(warranty.quoteDate))}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarToday
                    sx={{ fontSize: '0.8rem', color: 'success.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Fecha de Inicio
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {formatDate(new Date(warranty.startDate))}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarToday
                    sx={{ fontSize: '0.8rem', color: 'error.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Fecha de Término
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {formatDate(new Date(warranty.endDate))}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 1.5,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
              mt: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1rem',
                  lineHeight: 1.1,
                }}
              >
                Información del Mandante
              </Typography>
              <Chip
                label="Detalles"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Person
                      sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Razón Social
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {principal?.business.name ?? 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AssignmentInd
                      sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      RUT
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {warranty.principalRut}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255, 152, 0, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocationOn
                      sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Dirección
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatAddress(principal?.address)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {principal?.unit && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        backgroundColor: 'rgba(156, 39, 176, 0.1)',
                        borderRadius: '50%',
                        p: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BusinessCenter
                        sx={{ fontSize: '0.8rem', color: 'secondary.main' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem' }}
                      >
                        Unidad de Negocio
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {principal.unit.name} ({principal.unit.unitCode})
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {showDocument && (
        <Box sx={{ mt: 3 }}>
          <PaymentDocument document={warranty.documents[0]} />
        </Box>
      )}

      {showCalculator && (
        <Box sx={{ mt: 3 }}>
          <RiskPremiumCalculator
            amount={warranty.quotedAmount}
            days={daysDifference}
            quotedCurrency={warranty.quotedCurrency}
            type={warranty.type}
            warrantyRates={{
              variableRate: warranty.operationCostVariable,
              fixedRate: warranty.operationCostFixed,
              variableDiscount: warranty.discountRateVariable,
              fixedDiscount: warranty.discountRateFixed,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default WarrantySummary;
